/**
 * Canonical registry of device-scoped analytics events (ENG-3960 §F).
 *
 * Every event in this set is "device-scoped": it concerns a specific physical
 * device and therefore MUST carry the canonical cohort props
 * (device_model_id + device_brand_id) so cohort coverage is guaranteed
 * structurally rather than per call site. The A1 audit checks §F coverage
 * against this registry, so it is the single source of truth — adding a new
 * device-scoped event means adding it here.
 *
 * Per rule: think-in-systems — one canonical list, composed from EVENT_NAMES so
 * the registry can never drift to a stale string literal.
 * Per rule: investigate-before-adding — built from the existing EVENT_NAMES
 * constants + the ENG-3479/3495 families recorded in CANONICAL_INVENTORY.md.
 */

import { EVENT_NAMES } from './event-names.js';
import type { EventName } from './event-names.js';

/**
 * Device-scoped event names. Membership = "this event describes a device and is
 * eligible for automatic device-cohort enrichment".
 */
export const DEVICE_EVENTS: ReadonlySet<EventName> = new Set<EventName>([
  // Lifecycle
  EVENT_NAMES.DEVICE_ADD,
  EVENT_NAMES.DEVICE_DELETE,
  EVENT_NAMES.DEVICE_RESTORE,
  EVENT_NAMES.DEVICE_SHADOWED,
  EVENT_NAMES.DEVICE_AUTHENTICATE,
  EVENT_NAMES.DEVICE_AUTH_INVALID,
  // Add / discovery flow
  EVENT_NAMES.DEVICE_CLICK_DISCOVERED,
  EVENT_NAMES.DEVICE_CLICK_MANUAL_ADD,
  EVENT_NAMES.DEVICE_CONTINUE_FROM_BRAND,
  EVENT_NAMES.DEVICE_CONTINUE_FROM_CONTROL,
  EVENT_NAMES.DEVICE_CONTINUE_FROM_VIDEO,
  EVENT_NAMES.DEVICE_CONTINUE_TO_VIDEO,
  EVENT_NAMES.DEVICE_DISCOVERY_DEVICE_FOUND,
  EVENT_NAMES.DEVICE_DISCOVERY_NO_DEVICES_FOUND,
  // ENG-4312 canonical twins: membership mirrors the legacy names above so the
  // dual-emitted canonical events ride the SAME enrichment path as their legacy
  // aliases (identical property bags during the parity window).
  EVENT_NAMES.DISCOVERY_DEVICE_FOUND,
  EVENT_NAMES.DISCOVERY_EMPTY,
  EVENT_NAMES.DEVICE_SELECT_BRAND,
  EVENT_NAMES.DEVICE_SELECT_MODEL,
  // Media + controls connection (ENG-3495)
  EVENT_NAMES.DEVICE_MEDIA_CONNECT_ERROR,
  EVENT_NAMES.DEVICE_MEDIA_CONNECT_SUCCESS,
  EVENT_NAMES.DEVICE_MEDIA_QUALITY_PING,
  EVENT_NAMES.DEVICE_MEDIA_STREAMING_ERROR,
  EVENT_NAMES.DEVICE_MEDIA_STREAMING_SUCCESS,
  EVENT_NAMES.DEVICE_CONTROLS_CONNECT_ERROR,
  EVENT_NAMES.DEVICE_CONTROLS_CONNECT_SUCCESS,
  // Canonical media + controls names (ENG-4313). Membership must cover the NEW names too
  // so the dual-emitted canonical events get the same device-cohort auto-enrichment
  // (device_model_id + device_brand_id) as their legacy aliases during the migration window.
  EVENT_NAMES.MEDIA_CONNECT_FAILED,
  EVENT_NAMES.MEDIA_CONNECT_SUCCEEDED,
  EVENT_NAMES.MEDIA_STREAM_FAILED,
  EVENT_NAMES.MEDIA_STREAM_SUCCEEDED,
  EVENT_NAMES.MEDIA_QUALITY_PING,
  EVENT_NAMES.CONTROLS_CONNECT_FAILED,
  EVENT_NAMES.CONTROLS_CONNECT_SUCCEEDED,
  // Movement
  EVENT_NAMES.DEVICE_MOVE_DIRECT,
  EVENT_NAMES.DEVICE_MOVE_START,
  EVENT_NAMES.DEVICE_MOVE_END,
  // Settings
  EVENT_NAMES.DEVICE_SETTINGS_CHANGE,
  // Auto-tracking (device-scoped per ENG-3495)
  EVENT_NAMES.AUTO_TRACKING_STARTED,
  EVENT_NAMES.AUTO_TRACKING_STOPPED,
  EVENT_NAMES.AUTO_TRACKING_ACTIVATED,
  EVENT_NAMES.AUTO_TRACKING_SUBJECT_ACQUIRED,
  EVENT_NAMES.AUTO_TRACKING_SUBJECT_LOST
]);

/**
 * Whether an event is device-scoped and therefore eligible for automatic
 * device-cohort enrichment (device_model_id + device_brand_id).
 */
export const isDeviceScopedEvent = (name: EventName): boolean => DEVICE_EVENTS.has(name);
